/**
 * How big a pair this browser will take on, from measurement rather than taste.
 *
 * The analysis runs in a worker and the result graph is roughly the size of the
 * inputs (`analysis-scale.test.ts` pins that), which then exists two or three
 * times over: parsed input in the worker, the graph, its structured clone on the
 * main thread, and the copy IndexedDB serializes. So the pair's own bytes decide
 * whether the tab survives.
 *
 * MEASURED in Chromium against synthetic pairs built by repeating the real
 * Bellingham records (about 2.9 KB each), on the dev server, 2026-09-09:
 *
 * | Records (each side) | Pair on disk | Result |
 * |---|---|---|
 * | 500 (the real pair) | 2.9 MB | analysed in well under a second |
 * | 10,000 | 57.8 MB | analysed in 4.0 s |
 * | 20,000 | 115.4 MB | never finished — abandoned after 8 minutes, tab wedged, no error |
 *
 * The failure at 115 MB is the bad kind: no exception, no crash event, nothing
 * to report to the person waiting. That is why the ceiling is a refusal rather
 * than a warning, and why it sits at the largest size actually observed to
 * finish rather than somewhere hopeful in between. A pair over it is not
 * unanalysable — `npm run analyze` does the same analysis in node, where the
 * heap can be raised — it is just not something to attempt in a tab.
 */

/** Per file. Above this the pair is unusual enough to say so before running. */
export const PAIR_SIZE_WARN_BYTES = 10_000_000;

/**
 * Combined, both files. 57.8 MB was measured to analyse in 4 s; 115.4 MB never
 * returned. The ceiling is the measured success, not the midpoint of a gap
 * nobody has stood in.
 */
export const PAIR_SIZE_REFUSE_BYTES = 57_800_000;

export type PairSizeAssessment =
  | { level: "ok" }
  | { level: "warn" | "refuse"; message: string };

const OK: PairSizeAssessment = { level: "ok" };

export function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`;
  return `${bytes} bytes`;
}

/**
 * What to do about a pair of this size. Sizes come from the File objects, so
 * this is known before a byte is parsed.
 */
export function assessPairSize(baselineBytes: number | null, latestBytes: number | null): PairSizeAssessment {
  if (baselineBytes === null || latestBytes === null) return OK;
  const combined = baselineBytes + latestBytes;

  if (combined > PAIR_SIZE_REFUSE_BYTES) {
    return {
      level: "refuse",
      message:
        `These two files total ${formatBytes(combined)}, over the ${formatBytes(PAIR_SIZE_REFUSE_BYTES)} this ` +
        "browser can analyse. A pair twice that size was measured to wedge the tab with no error and no result, " +
        "so this refuses rather than starting something that cannot report its own failure. Run the same analysis " +
        "outside the browser with `npm run analyze`, or split the export."
    };
  }

  const largest = Math.max(baselineBytes, latestBytes);
  if (largest > PAIR_SIZE_WARN_BYTES) {
    return {
      level: "warn",
      message:
        `The larger file is ${formatBytes(largest)} — well above a typical run (the real Bellingham pair is ` +
        "2.9 MB for both). The analysis holds several copies of a graph this size in memory and will take a few " +
        "seconds; nothing is wrong, but expect the tab to be busy."
    };
  }

  return OK;
}
